import axios from "axios";
import { useEffect, useState } from "react";
import { uploadImage } from "../../common/uploadImage";
import toast from "react-hot-toast";
import {
  Plus,
  Upload,
  Image,
  X,
  ChevronLeft,
  Save,
  Loader2,
  Link,
  List,
  Info,
  Package,
  CheckCircle,
  XCircle,
  Type,
  FileText,
  Grid,
  Settings,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],

    [{ color: [] }, { background: [] }],

    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "link",
  "image",
];

const ProductCreate = () => {
  const API = process.env.REACT_APP_API_URL;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const [form, setForm] = useState({
    heading: "",
    slug: "",
    description: "",
    mainImage: "",
    smallImages: [],
    bulletDescription: [],
    additionalPoints: [],
    moreProductInfo: [],
    category: "",
    isActive: true,
  });

  /* ---------------- FETCH CATEGORIES ---------------- */
  useEffect(() => {
    axios.get(`${API}/categories`).then((res) => {
      setCategories(res.data.categories || res.data);
    });
  }, []);

  /* ---------------- HANDLERS ---------------- */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleMainImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    toast.promise(
      uploadImage(file).then((url) => {
        setForm({ ...form, mainImage: url });
        return url;
      }),
      {
        loading: "Uploading main image...",
        success: "Main image uploaded!",
        error: "Failed to upload image",
      }
    );
  };

  const handleSmallImages = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    toast.promise(
      Promise.all(files.map(uploadImage)).then((urls) => {
        setForm({ ...form, smallImages: [...form.smallImages, ...urls] });
        return urls;
      }),
      {
        loading: "Uploading images...",
        success: `${files.length} images uploaded!`,
        error: "Failed to upload images",
      }
    );
  };

  const removeSmallImage = (index) => {
    const newImages = [...form.smallImages];
    newImages.splice(index, 1);
    setForm({ ...form, smallImages: newImages });
  };

  /* ---------------- BULLET DESCRIPTION ---------------- */
  const addBullet = () =>
    setForm({ ...form, bulletDescription: [...form.bulletDescription, ""] });

  const updateBullet = (i, value) => {
    const bullets = [...form.bulletDescription];
    bullets[i] = value;
    setForm({ ...form, bulletDescription: bullets });
  };

  const removeBullet = (i) => {
    const bullets = [...form.bulletDescription];
    bullets.splice(i, 1);
    setForm({ ...form, bulletDescription: bullets });
  };

  /* ---------------- ADDITIONAL POINTS ---------------- */
  const addAdditionalPoint = () => {
    setForm({
      ...form,
      additionalPoints: [
        ...form.additionalPoints,
        { title: "", description: "", icon: "" },
      ],
    });
  };

  const updateAdditionalPoint = (i, field, value) => {
    const points = [...form.additionalPoints];
    points[i][field] = value;
    setForm({ ...form, additionalPoints: points });
  };

  const uploadAdditionalIcon = async (i, file) => {
    if (!file) return;

    toast.promise(
      uploadImage(file).then((url) => {
        updateAdditionalPoint(i, "icon", url);
        return url;
      }),
      {
        loading: "Uploading icon...",
        success: "Icon uploaded!",
        error: "Failed to upload icon",
      }
    );
  };

  const removeAdditionalPoint = (i) => {
    const points = [...form.additionalPoints];
    points.splice(i, 1);
    setForm({ ...form, additionalPoints: points });
  };

  /* ---------------- MORE PRODUCT INFO ---------------- */
  const addMoreInfo = () => {
    setForm({
      ...form,
      moreProductInfo: [
        ...form.moreProductInfo,
        { image: "", title: "", description: "" },
      ],
    });
  };

  const updateMoreInfo = (i, field, value) => {
    const info = [...form.moreProductInfo];
    info[i][field] = value;
    setForm({ ...form, moreProductInfo: info });
  };

  const uploadMoreInfoImage = async (i, file) => {
    if (!file) return;

    toast.promise(
      uploadImage(file).then((url) => {
        updateMoreInfo(i, "image", url);
        return url;
      }),
      {
        loading: "Uploading image...",
        success: "Image uploaded!",
        error: "Failed to upload image",
      }
    );
  };

  const removeMoreInfo = (i) => {
    const info = [...form.moreProductInfo];
    info.splice(i, 1);
    setForm({ ...form, moreProductInfo: info });
  };

  const createProduct = async () => {
    if (
      !form.heading ||
      !form.description ||
      !form.mainImage ||
      !form.category
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/product`, form);
      toast.success("Product created successfully!");
      setTimeout(() => window.history.back(), 1000);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
              >
                <ChevronLeft size={20} />
                Back to Products
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Create New Product
              </h1>
              <p className="text-gray-600 mt-2">
                Fill in the details below to add a new product to your catalog
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label className="inline-flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                  <div
                    className={`block w-14 h-8 rounded-full ${
                      form.isActive ? "bg-green-500" : "bg-gray-300"
                    }`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${
                      form.isActive ? "transform translate-x-6" : ""
                    }`}
                  ></div>
                </div>
                <span className="ml-3 text-gray-700 font-medium">
                  {form.isActive ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {["basic", "images", "content"].map((tab, index) => (
              <div key={tab} className="flex items-center">
                <button
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                    activeTab === tab
                      ? "bg-indigo-600 text-white shadow-lg"
                      : "bg-white text-gray-400 border border-gray-200"
                  }`}
                >
                  {index + 1}
                </button>
                {index < 3 && (
                  <div
                    className={`w-24 h-1 mx-2 ${
                      activeTab === tab ? "bg-indigo-200" : "bg-gray-200"
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {[
              { id: "basic", label: "Basic Info", icon: <Type size={18} /> },
              { id: "images", label: "Media", icon: <Image size={18} /> },
              { id: "content", label: "Content", icon: <FileText size={18} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-indigo-600 text-white shadow-md"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Type size={22} />
                  Basic Information
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Name *
                    </label>
                    <div className="relative">
                      <input
                        name="heading"
                        placeholder="Enter product name"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                        value={form.heading}
                        onChange={handleChange}
                      />
                      <Type
                        className="absolute right-3 top-3.5 text-gray-400"
                        size={20}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slug
                    </label>
                    <div className="relative">
                      <input
                        name="slug"
                        placeholder="product-slug-name"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                        value={form.slug}
                        onChange={handleChange}
                      />
                      <Link
                        className="absolute right-3 top-3.5 text-gray-400"
                        size={20}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>

                    <div className="bg-white rounded-xl border border-gray-200">
                      <ReactQuill
                        theme="snow"
                        value={form.description}
                        onChange={(value) =>
                          setForm({ ...form, description: value })
                        }
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Describe your product in detail..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <div className="relative">
                      <select
                        name="category"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none appearance-none"
                        value={form.category}
                        onChange={handleChange}
                      >
                        <option value="">Select a category</option>
                        {categories.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <Package
                        className="absolute right-3 top-3.5 text-gray-400 pointer-events-none"
                        size={20}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Images Tab */}
            {activeTab === "images" && (
              <div className="space-y-6">
                {/* Main Image */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Image size={22} />
                    Main Product Image
                  </h2>

                  <div className="text-center">
                    {form.mainImage ? (
                      <div className="relative">
                        <img
                          src={form.mainImage}
                          alt="Main product"
                          className="w-full max-w-md h-64 object-cover rounded-xl mx-auto"
                        />
                        <button
                          onClick={() => setForm({ ...form, mainImage: "" })}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-2xl p-12 hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                          <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-lg font-medium text-gray-700">
                            Click to upload main image
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            PNG, JPG or WebP (Max 5MB)
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleMainImage}
                          accept="image/*"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Small Images */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">
                    Gallery Images
                  </h2>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                    {form.smallImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeSmallImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}

                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex flex-col items-center justify-center hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                        <Plus className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add Image</span>
                      </div>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleSmallImages}
                        accept="image/*"
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Content Tab */}
            {activeTab === "content" && (
              <div className="space-y-6">
                {/* Bullet Points */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <List size={22} />
                    Key Features
                  </h2>

                  {form.bulletDescription.map((bullet, i) => (
                    <div key={i} className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <div className="relative">
                          <input
                            placeholder="Enter feature description"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                            value={bullet}
                            onChange={(e) => updateBullet(i, e.target.value)}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => removeBullet(i)}
                        className="p-3 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={addBullet}
                    className="inline-flex items-center gap-2 px-4 py-3 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors"
                  >
                    <Plus size={20} />
                    Add Feature
                  </button>
                </div>

                {/* Additional Points */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Info size={22} />
                    Additional Information
                  </h2>

                  {form.additionalPoints.map((point, i) => (
                    <div
                      key={i}
                      className="border border-gray-200 rounded-xl p-4 mb-4"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="font-medium text-gray-700">
                          Point {i + 1}
                        </h3>
                        <button
                          onClick={() => removeAdditionalPoint(i)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <input
                          placeholder="Title"
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                          value={point.title}
                          onChange={(e) =>
                            updateAdditionalPoint(i, "title", e.target.value)
                          }
                        />

                        <textarea
                          placeholder="Description"
                          rows="2"
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
                          value={point.description}
                          onChange={(e) =>
                            updateAdditionalPoint(
                              i,
                              "description",
                              e.target.value
                            )
                          }
                        />

                        <div>
                          <label className="block text-sm text-gray-600 mb-2">
                            Icon
                          </label>
                          {point.icon ? (
                            <div className="flex items-center gap-3">
                              <img
                                src={point.icon}
                                alt="Icon"
                                className="w-12 h-12 rounded-lg"
                              />
                              <button
                                onClick={() =>
                                  updateAdditionalPoint(i, "icon", "")
                                }
                                className="text-red-500 hover:text-red-600"
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400">
                                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                <span className="text-sm text-gray-600">
                                  Upload Icon
                                </span>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) =>
                                  uploadAdditionalIcon(i, e.target.files[0])
                                }
                                accept="image/*"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={addAdditionalPoint}
                    className="inline-flex items-center gap-2 px-4 py-3 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors"
                  >
                    <Plus size={20} />
                    Add Additional Point
                  </button>
                </div>

                {/* More Product Info */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Grid size={22} />
                    Detailed Information
                  </h2>

                  {form.moreProductInfo.map((info, i) => (
                    <div
                      key={i}
                      className="border border-gray-200 rounded-xl p-4 mb-4"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="font-medium text-gray-700">
                          Section {i + 1}
                        </h3>
                        <button
                          onClick={() => removeMoreInfo(i)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <input
                          placeholder="Title"
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                          value={info.title}
                          onChange={(e) =>
                            updateMoreInfo(i, "title", e.target.value)
                          }
                        />
                        <div className="bg-white border border-gray-200 rounded-lg">
                          <ReactQuill
                            theme="snow"
                            value={info.description}
                            onChange={(value) =>
                              updateMoreInfo(i, "description", value)
                            }
                            placeholder="Description"
                            className="min-h-[120px]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-600 mb-2">
                            Image
                          </label>
                          {info.image ? (
                            <div className="flex items-center gap-3">
                              <img
                                src={info.image}
                                alt="Section"
                                className="w-20 h-20 rounded-lg object-cover"
                              />
                              <button
                                onClick={() => updateMoreInfo(i, "image", "")}
                                className="text-red-500 hover:text-red-600"
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400">
                                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                <span className="text-sm text-gray-600">
                                  Upload Image
                                </span>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) =>
                                  uploadMoreInfoImage(i, e.target.files[0])
                                }
                                accept="image/*"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={addMoreInfo}
                    className="inline-flex items-center gap-2 px-4 py-3 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors"
                  >
                    <Plus size={20} />
                    Add More Information
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Preview & Actions */}
          <div className="space-y-6">
            {/* Preview Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 ">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Product Preview
              </h2>

              {form.heading || form.mainImage ? (
                <div className="space-y-4">
                  {form.mainImage && (
                    <img
                      src={form.mainImage}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                  )}

                  {form.heading && (
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {form.heading}
                      </h3>
                      {form.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                          {form.description}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status:</span>
                      <span
                        className={`flex items-center gap-1 ${
                          form.isActive ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {form.isActive ? (
                          <CheckCircle size={16} />
                        ) : (
                          <XCircle size={16} />
                        )}
                        {form.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {form.category && (
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-gray-500">Category:</span>
                        <span className="font-medium text-gray-900">
                          {categories.find((c) => c._id === form.category)
                            ?.name || "Selected"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Start filling the form to see preview
                  </p>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Quick Stats
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Features:</span>
                  <span className="font-semibold">
                    {form.bulletDescription.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Gallery Images:</span>
                  <span className="font-semibold">
                    {form.smallImages.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Additional Points:</span>
                  <span className="font-semibold">
                    {form.additionalPoints.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Info Sections:</span>
                  <span className="font-semibold">
                    {form.moreProductInfo.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={createProduct}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Creating Product...
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Create Product
                    </>
                  )}
                </button>

                <button
                  onClick={() => window.history.back()}
                  className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCreate;

// import axios from "axios";
// import { useEffect, useState } from "react";
// import { uploadImage } from "../../common/uploadImage";
// import toast from "react-hot-toast";

// const ProductCreate = () => {
//   const API = process.env.REACT_APP_API_URL;

//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [form, setForm] = useState({
//     heading: "",
//     slug: "",
//     description: "",
//     mainImage: "",
//     smallImages: [],
//     bulletDescription: [],
//     additionalPoints: [],
//     moreProductInfo: [],
//     category: "",
//     isActive: true,
//   });

//   /* ---------------- FETCH CATEGORIES ---------------- */
//   useEffect(() => {
//     axios.get(`${API}/categories`).then((res) => {
//       setCategories(res.data.categories || res.data);
//     });
//   }, []);

//   /* ---------------- HANDLERS ---------------- */
//   const handleChange = (e) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };

//   const handleMainImage = async (e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;

//     const url = await uploadImage(file);
//     setForm({ ...form, mainImage: url });
//   };

//   const handleSmallImages = async (e) => {
//     const files = Array.from(e.target.files);
//     const urls = await Promise.all(files.map(uploadImage));
//     setForm({ ...form, smallImages: [...form.smallImages, ...urls] });
//   };

//   /* ---------------- BULLET DESCRIPTION ---------------- */
//   const addBullet = () =>
//     setForm({ ...form, bulletDescription: [...form.bulletDescription, ""] });

//   const updateBullet = (i, value) => {
//     const bullets = [...form.bulletDescription];
//     bullets[i] = value;
//     setForm({ ...form, bulletDescription: bullets });
//   };

//   /* ---------------- ADDITIONAL POINTS ---------------- */
//   const addAdditionalPoint = () => {
//     setForm({
//       ...form,
//       additionalPoints: [
//         ...form.additionalPoints,
//         { title: "", description: "", icon: "" },
//       ],
//     });
//   };

//   const updateAdditionalPoint = (i, field, value) => {
//     const points = [...form.additionalPoints];
//     points[i][field] = value;
//     setForm({ ...form, additionalPoints: points });
//   };

//   const uploadAdditionalIcon = async (i, file) => {
//     const url = await uploadImage(file);
//     updateAdditionalPoint(i, "icon", url);
//   };

//   /* ---------------- MORE PRODUCT INFO ---------------- */
//   const addMoreInfo = () => {
//     setForm({
//       ...form,
//       moreProductInfo: [
//         ...form.moreProductInfo,
//         { image: "", title: "", description: "" },
//       ],
//     });
//   };

//   const updateMoreInfo = (i, field, value) => {
//     const info = [...form.moreProductInfo];
//     info[i][field] = value;
//     setForm({ ...form, moreProductInfo: info });
//   };

//   const uploadMoreInfoImage = async (i, file) => {
//     const url = await uploadImage(file);
//     updateMoreInfo(i, "image", url);
//   };

//   const createProduct = async () => {
//     if (
//       !form.heading ||
//       !form.description ||
//       !form.mainImage ||
//       !form.category
//     ) {
//       toast.error("Please fill all required fields");
//       return;
//     }

//     setLoading(true);

//     try {
//       await axios.post(`${API}/product`, form);

//       toast.success("Service Added Successfully");
//       window.history.back();
//     } catch (error) {
//       console.error(error);

//       toast.error(error.response?.data?.message || "Failed to create product");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ---------------- UI ---------------- */
//   return (
//     <div className="p-6 max-w-5xl mx-auto space-y-6">
//       <h1 className="text-2xl font-bold">Create Product</h1>

//       <input
//         name="heading"
//         placeholder="Heading"
//         className="input"
//         value={form.heading}
//         onChange={handleChange}
//       />

//       <input
//         name="slug"
//         placeholder="Slug"
//         className="input"
//         value={form.slug}
//         onChange={handleChange}
//       />

//       <textarea
//         name="description"
//         placeholder="Description"
//         className="input"
//         value={form.description}
//         onChange={handleChange}
//       />

//       {/* CATEGORY */}
//       <select
//         name="category"
//         className="input"
//         value={form.category}
//         onChange={handleChange}
//       >
//         <option value="">Select Category</option>
//         {categories.map((c) => (
//           <option key={c._id} value={c._id}>
//             {c.name}
//           </option>
//         ))}
//       </select>

//       {/* MAIN IMAGE */}
//       <div>
//         <label>Main Image</label>
//         <input type="file" onChange={handleMainImage} />
//         {form.mainImage && <img src={form.mainImage} className="h-24 mt-2" />}
//       </div>

//       {/* SMALL IMAGES */}
//       <div>
//         <label>Small Images</label>
//         <input type="file" multiple onChange={handleSmallImages} />
//       </div>

//       {/* BULLET DESCRIPTION */}
//       <div>
//         <h3 className="font-semibold">Bullet Description</h3>
//         {form.bulletDescription.map((b, i) => (
//           <input
//             key={i}
//             className="input mt-2"
//             value={b}
//             onChange={(e) => updateBullet(i, e.target.value)}
//           />
//         ))}
//         <button onClick={addBullet} className="btn mt-2">
//           + Add Bullet
//         </button>
//       </div>

//       {/* ADDITIONAL POINTS */}
//       <div>
//         <h3 className="font-semibold">Additional Points</h3>
//         {form.additionalPoints.map((p, i) => (
//           <div key={i} className="border p-3 mt-2 space-y-2">
//             <input
//               placeholder="Title"
//               className="input"
//               value={p.title}
//               onChange={(e) =>
//                 updateAdditionalPoint(i, "title", e.target.value)
//               }
//             />
//             <textarea
//               placeholder="Description"
//               className="input"
//               value={p.description}
//               onChange={(e) =>
//                 updateAdditionalPoint(i, "description", e.target.value)
//               }
//             />
//             <input
//               type="file"
//               onChange={(e) => uploadAdditionalIcon(i, e.target.files[0])}
//             />
//           </div>
//         ))}
//         <button onClick={addAdditionalPoint} className="btn mt-2">
//           + Add Additional Point
//         </button>
//       </div>

//       {/* MORE PRODUCT INFO */}
//       <div>
//         <h3 className="font-semibold">More Product Info</h3>
//         {form.moreProductInfo.map((m, i) => (
//           <div key={i} className="border p-3 mt-2 space-y-2">
//             <input
//               placeholder="Title"
//               className="input"
//               value={m.title}
//               onChange={(e) => updateMoreInfo(i, "title", e.target.value)}
//             />
//             <textarea
//               placeholder="Description"
//               className="input"
//               value={m.description}
//               onChange={(e) => updateMoreInfo(i, "description", e.target.value)}
//             />
//             <input
//               type="file"
//               onChange={(e) => uploadMoreInfoImage(i, e.target.files[0])}
//             />
//           </div>
//         ))}
//         <button onClick={addMoreInfo} className="btn mt-2">
//           + Add More Info
//         </button>
//       </div>

//       <button
//         onClick={createProduct}
//         disabled={loading}
//         className="bg-green-600 text-white px-6 py-2 rounded"
//       >
//         {loading ? "Creating..." : "Create Product"}
//       </button>
//     </div>
//   );
// };

// export default ProductCreate;
